import { Center, Resize, Svg } from "@react-three/drei";
import * as React from "react";
import * as THREE from "three";
import { Hotspot, Scene, TourData } from "../../../../../Types";
import { useFrame } from "@react-three/fiber";
import { Interactive, useController } from "@react-three/xr";

import { useXRPopupStore } from "../XRHotspots";
import {
  useCurrentSceneStore,
  useDataRootStore,
} from "../../../../../api/Zustand";
import XRHotspotLabel from "./XRHotspotLabel";
import { getLanguageFile } from "../../../../common/GetLanguageFile";
import { useMockEscapeRoomStore } from "../../../escape-room/zustand";
import { useCursorShrinkingStore } from "../../UI/CursorNew";
import { useGLRendererStore } from "../../../../../state management/zustand/gl-stores";

const center = new THREE.Vector3(0, 0, 0);

let timeout: NodeJS.Timeout;
let timeout2: NodeJS.Timeout;

function XRWaypoint(props: {
  pos: THREE.Vector3 | number[];
  hs: Hotspot;
  tourData: TourData;
}) {
  const xrPop = useXRPopupStore();

  const { pos, hs, tourData } = props;

  const iconRef = React.useRef<THREE.Mesh>(null);

  const svgRef = React.useRef<THREE.Object3D<Event>>(null);
  const pi = Math.PI;

  const [isHovering, setHovering] = React.useState<string | null>();
  const groupRef = React.useRef<THREE.Group>(null);

  const gazeController = useController("none");
  const leftController = useController("left");
  const rightController = useController("right");

  useFrame(() => {
    if (isHovering === hs.id) {
      iconRef.current?.scale.lerp(new THREE.Vector3(1, 1, 1), 0.1);
    } else {
      iconRef.current?.scale.lerp(new THREE.Vector3(0.8, 0.8, 0.8), 0.1);
    }
  });

  const { root } = useDataRootStore();

  const targetScene = React.useMemo(() => {
    return tourData.scenes.find((one) => one.id === hs.target);
  }, [tourData.scenes, hs.target]);

  const cursor = useCursorShrinkingStore();

  if (
    root &&
    !xrPop.isOpen &&
    tourData.scenes.find((one: Scene) => one.id === hs.target)
  )
    return (
      <group
        ref={groupRef}
        scale={1}
        onUpdate={(self) => self.lookAt(center)}
        position={pos as THREE.Vector3}
        visible={!xrPop.isOpen}
      >
        <Interactive
          key={hs.x + hs.y}
          onHover={() => {
            setHovering(hs.id);
            if (
              hs.type === "transition" &&
              tourData.scenes.find((one) => one.id === hs.target)
            ) {
              if (gazeController && !leftController && !rightController) {
                clearTimeout(timeout);
                clearTimeout(timeout2);

                timeout = setTimeout(() => {
                  cursor.setShrinking(true);
                  timeout2 = setTimeout(() => {
                    cursor.setShrinking(false);
                    XRNavigateWithLocksCheck(hs.target as number, [
                      hs.x,
                      hs.y,
                      hs.z,
                    ]);
                  }, 2e3);
                }, 1e3);
              }
            }
          }}
          onBlur={() => {
            setHovering(undefined);
            cursor.setShrinking(false);
            clearTimeout(timeout);
            clearTimeout(timeout2);
          }}
          onSelect={() => {
            if (
              hs.type === "transition" &&
              tourData.scenes.find((one: Scene) => one.id === hs.target)
            ) {
              cursor.setShrinking(false);
              XRNavigateWithLocksCheck(hs.target as number, [hs.x, hs.y, hs.z]);
            }
          }}
        >
          <group scale={gazeController ? 1.6 : 0.6}>
            <mesh ref={iconRef} scale={0.8}>
              <mesh
                name="hotspot"
                renderOrder={6}
                rotation={[
                  0,
                  0,
                  hs.dir === "B"
                    ? pi / 2
                    : hs.dir === "T"
                    ? -pi / 2
                    : hs.dir === "R"
                    ? pi
                    : 0,
                ]}
              >
                <planeGeometry args={[30, 30]}></planeGeometry>
                <meshBasicMaterial visible={false} />
                <Center>
                  <Resize width={true} height={true} scale={30}>
                    <Svg
                      name="SVG"
                      ref={svgRef}
                      src={root + "/svg/arrow.svg"}
                      fillMaterial={{
                        depthTest: false,
                      }}
                      strokeMaterial={{
                        color: "black",
                        depthTest: false,
                      }}
                      strokeMeshProps={{ position: [0, 0, 1] }}
                      scale={[-1, 1, 1]}
                      renderOrder={2}
                    />
                  </Resize>
                </Center>
              </mesh>
            </mesh>

            <XRHotspotLabel hoverState={isHovering === hs.id}>
              {targetScene?.title || getLanguageFile("viewer.scene_deleted")}
            </XRHotspotLabel>
          </group>
        </Interactive>
      </group>
    );
}

export default XRWaypoint;

export function XRNavigateWithLocksCheck(
  id: number,
  dir: THREE.Vector3 | number[]
) {
  const { hardLockQuestions, softLockQuestions } =
    useMockEscapeRoomStore.getState();

  const { state } = useGLRendererStore.getState();

  const cameraDir = new THREE.Vector3();

  state?.camera.getWorldDirection(cameraDir);

  const { setCurrentSceneID, currentSceneID } = useCurrentSceneStore.getState();
  const { openCustomPop } = useXRPopupStore.getState();

  if (currentSceneID === id) return;

  if (hardLockQuestions.length) {
    openCustomPop(id, dir, 1);
  } else if (softLockQuestions.length) {
    openCustomPop(id, dir, 0);
  } else {
    setCurrentSceneID(id);
  }
}
